/**
 * Map technical / JWT auth errors to user-centric copy.
 * Also covers older backend messages that may still be cached.
 */
const SESSION_MESSAGE =
  "Your session has expired. Please log in again.";
const PERMISSION_MESSAGE =
  "You don't have permission to do this. Please contact your admin.";

const TECHNICAL_AUTH_PATTERNS = [
  /token has expired/i,
  /jwt expired/i,
  /token expired/i,
  /invalid token/i,
  /token missing/i,
  /authorization token required/i,
  /authentication failed/i,
  /authentication required/i,
  /invalid token type/i,
  /session has expired/i,
  /session is no longer valid/i,
  /unable to verify your session/i,
  /please log in to continue/i,
];

export function isSessionAuthMessage(msg = "") {
  return TECHNICAL_AUTH_PATTERNS.some((re) => re.test(String(msg)));
}

export function isPermissionMessage(msg = "") {
  const text = String(msg).toLowerCase();
  return (
    text.includes("insufficient permissions") ||
    text.includes("do not have") ||
    text.includes("don't have permission") ||
    text.includes("access to")
  );
}

export function humanizeAuthMessage(msg, fallback = "Something went wrong") {
  if (!msg) return fallback;
  if (isSessionAuthMessage(msg)) return SESSION_MESSAGE;
  if (isPermissionMessage(msg)) return PERMISSION_MESSAGE;
  return msg;
}

/**
 * Extract a user-facing message from an API error payload.
 */
export function getApiErrorMessage(err, fallback = "Something went wrong") {
  const raw =
    (Array.isArray(err?.errors) && err.errors[0]?.msg) ||
    err?.message ||
    err?.msg ||
    (typeof err === "string" ? err : null) ||
    fallback;

  return humanizeAuthMessage(raw, fallback);
}

/**
 * Rewrite error payload so any toast using errors[0].msg is user-centric.
 */
export function sanitizeApiErrorPayload(payload, status) {
  if (!payload || typeof payload !== "object") {
    if (status === 401) {
      return { success: false, errors: [{ msg: SESSION_MESSAGE }] };
    }
    return payload;
  }

  const next = { ...payload };

  if (Array.isArray(next.errors)) {
    next.errors = next.errors.map((item) =>
      item?.msg
        ? { ...item, msg: humanizeAuthMessage(item.msg, item.msg) }
        : item
    );
  }

  if (next.message) {
    next.message = humanizeAuthMessage(next.message, next.message);
  }

  if (status === 401 && (!next.errors || !next.errors.length)) {
    next.errors = [{ msg: SESSION_MESSAGE }];
  }

  return next;
}
